<template>
  <div class="appointment-filter-bar" v-show="!isCollapsed">
    <div class="row">
      <div class="col-6">
        <div class="filter-item">
          <div class="label">ผู้ให้บริการ</div>
          <provider-selector v-model="value.provider"></provider-selector>
        </div>
        <div class="filter-item">
          <div class="label">บริการ</div>
          <service-select
            :provider="value.provider"
            v-model="value.service"
          ></service-select>
        </div>
        <div class="filter-item">
          <div class="label">ลูกค้า</div>
          <user-select v-model="value.user"></user-select>
        </div>
        <div class="filter-item">
          <div class="label">โครงการ</div>
          <project-select v-model="value.project"></project-select>
        </div>
        <div class="filter-item">
          <div class="label">บ้านเลขที่</div>
          <el-select
            class="select"
            v-model="value.home"
            filterable
            clearable
            placeholder="เลือกบ้าน"
            :filter-method="handleFilterHomes"
            valueKey="homeId"
            labelKey="addressNo"
          >
            <el-option
              v-for="home in projectHomeOptions"
              :key="home.homeId"
              :value="home"
              :label="home.addressNo"
            ></el-option>
          </el-select>
        </div>
      </div>
      <div class="col-6">
        <div v-if="isProductWaitingFulfillPage">
          <div class="filter-item">
            <div class="label">วันซื้อเมื่อ</div>
            <el-date-picker
              class="date-picker"
              :value="purchasedAtRange"
              @input="handleChangeDate('purchasedAtRange', $event)"
              type="daterange"
              range-separator="ถึง"
            ></el-date-picker>
          </div>
          <div class="filter-item">
            <div class="label">สถานะ</div>
            <div class="status created">สินค้าซื้อใหม่</div>
          </div>
        </div>
        <div v-else>
          <div class="filter-item">
            <div class="label">วันนัดหมาย</div>
            <el-date-picker
              class="date-picker"
              :value="dateTimeRange"
              @input="handleChangeDate('dateTimeRange', $event)"
              type="daterange"
              range-separator="ถึง"
            ></el-date-picker>
          </div>
          <div class="filter-item">
            <div class="label">นัดเมื่อ</div>
            <el-date-picker
              class="date-picker"
              :value="createdAtRange"
              @input="handleChangeDate('createdAtRange', $event)"
              type="daterange"
              range-separator="ถึง"
            ></el-date-picker>
          </div>
          <div class="filter-item">
            <div class="label">สถานะ</div>
            <el-select v-model="value.status">
              <el-option
                v-for="status in statusOptions"
                :key="status.id"
                :label="status.label"
                :value="status.id"
              ></el-option>
            </el-select>
          </div>
        </div>
      </div>
    </div>
    <div class="text-right">
      <sc-button variant="primary" @click="handleClickSearchButton"
        >ค้นหา</sc-button
      >
    </div>
  </div>
</template>

<script>
import { DatePicker, Input, Option, Select } from "element-ui";
import { DateTime } from "luxon";
import ScButton from "@/lib/components/ScButton";
import ProviderSelector from "@/lib/components/ProviderSelector";
import ProjectSelect from "@/lib/components/ProjectSelect";
import ServiceSelect from "@/lib/components/ServiceSelect";
import UserSelect from "@/lib/components/UserSelect";
import { baanRueJaiClient } from "@/dependencies";

export default {
  components: {
    "el-input": Input,
    "el-select": Select,
    "el-option": Option,
    "el-date-picker": DatePicker,
    ProviderSelector,
    ProjectSelect,
    ServiceSelect,
    UserSelect,
    ScButton
  },
  props: ["value", "isProductWaitingFulfillPage"],
  data() {
    return {
      isCollapsed: false,
      homeQuery: "",
      projectHomes: [],
    };
  },
  watch: {
    ["value.project"](newVal, oldVal) {
      if (newVal === null) {
        return;
      }

      if (oldVal !== null && newVal.projectId === oldVal.projectId) {
        return;
      }

      this.updateProjectHomes();
    }
  },
  computed: {
    projectHomeOptions() {
      if (!this.homeQuery) {
        return this.projectHomes;
      }

      return this.projectHomes.filter((home) => {
        const [prefix, no] = home.addressNo.split("/");

        if (!no) {
          return home.addressNo.includes(this.homeQuery);
        }

        return no.includes(this.homeQuery);
      });
    },

    dateTimeRange() {
      if (!this.value.dateTimeRange) {
        return null;
      }

      const { from, to } = this.value.dateTimeRange;

      return [from.setZone("local").toJSDate(), to.setZone("local").toJSDate()];
    },
    createdAtRange() {
      if (!this.value.createdAtRange) {
        return null;
      }

      const { from, to } = this.value.createdAtRange;

      return [from.setZone("local").toJSDate(), to.setZone("local").toJSDate()];
    },
    statusOptions() {
      return [
        {
          id: "ALL",
          label: "ทั้งหมด"
        },
        {
          id: "CREATED",
          label: "รอผู้ให้บริการยืนยัน"
        },
        {
          id: "CONFIRMED",
          label: "รอรับบริการ"
        },
        {
          id: "CANCELLED",
          label: "ยกเลิก"
        },
        {
          id: "FULFILLED",
          label: "เสร็จสิ้น"
        }
      ];
    },
    purchasedAtRange() {
      if (!this.value.purchasedAtRange) {
        return null;
      }

      const { from, to } = this.value.purchasedAtRange;

      return [from.setZone("local").toJSDate(), to.setZone("local").toJSDate()];
    },
  },
  created() {
    this.setupForm()
  },
  methods: {
    async setupForm() {
      if (
        this.value
        && this.value.project
        && this.value.project.projectId
      ) {
        await this.updateProjectHomes(this.value.project.projectId)
      }
    },
    async updateProjectHomes(projectId) {
      const result = await this.fetchProjectHomes(projectId);

      this.homeQuery = "";
      this.projectHomes = result;
      this.projectHomeByIds = result.reduce((all, home) => {
        all[home.homeId] = home;
        return all;
      }, {});
      this.$emit("input", { ...this.value, homes: [], role: null });
    },
    async fetchProjectHomes() {
      const { projectId } = this.value.project;

      const response = await baanRueJaiClient.admin.memberManagement.projects.homes(
        projectId
      );

      return response.data;
    },
    async handleFilterHomes(query) {
      this.homeQuery = query;
    },
    handleChangeDate(key, value) {
      if (!value) {
        this.$emit("input", { ...this.value, [key]: null });
        return;
      }
      const [fromJSDate, toJSDate] = value;
      const from = DateTime.fromJSDate(fromJSDate).toUTC();
      const to = DateTime.fromJSDate(toJSDate).endOf("day").toUTC();

      this.$emit("input", {
        ...this.value,
        [key]: {
          from,
          to
        }
      });
    },
    handleClickSearchButton() {
      this.$emit("search");
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.appointment-filter-bar {
  border-bottom: $border;
  padding: ($space / 3) $space;
}

.filter-item {
  margin-bottom: 15px;
}

.label {
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.date-picker {
  width: 100%;
}

.status {
  line-height: 1.2;
  font-weight: bold;

  &.created {
    color: $secondary-color;
  }
}
</style>



// WEBPACK FOOTER //
// src/features/subscriptions/modules/appointments/components/AppointmentFilterBar.vue